import { z } from 'zod';

const UI_MESSAGE_ROLES = {
  assistant: 'assistant',
  system: 'system',
  user: 'user',
} as const;

const uiMessageRoleSchema = z.enum(UI_MESSAGE_ROLES);

/**
 * Tool-part lifecycle states, mirroring the AI SDK v6 `UIToolInvocation`
 * union. We persist these on `AgentMessage.data.uiParts` so the continuation
 * path can replay the conversation through `convertToModelMessages`.
 */
const uiToolPartStateSchema = z.enum([
  'input-streaming',
  'input-available',
  'approval-requested',
  'approval-responded',
  'output-available',
  'output-error',
  'output-denied',
]);

const toolApprovalSchema = z.object({
  approved: z.boolean().optional(),
  id: z.string(),
  reason: z.string().optional(),
  /**
   * True when the pause is gated on a system prerequisite (e.g. document
   * extraction) that resolves automatically. System approvals reuse the same
   * wait-then-execute protocol as human approvals but must never surface a
   * user-facing approval card.
   */
  system: z.boolean().optional(),
});

export type AiSdkUiToolPartType = 'dynamic-tool' | `tool-${string}`;

const uiToolPartTypeSchema = z.custom<AiSdkUiToolPartType>(
  (value): value is AiSdkUiToolPartType =>
    value === 'dynamic-tool' || (typeof value === 'string' && value.startsWith('tool-'))
);

const uiTextPartSchema = z.object({
  text: z.string(),
  type: z.literal('text'),
});

/**
 * A flat tool part as the AI SDK v6 represents it — `type` is either
 * `tool-${name}` (static) or `dynamic-tool`, with the lifecycle `state`
 * and an optional `approval` record. `nodeId` is Aline-only: it threads the
 * owning plan node so the continuation can resume the right node.
 */
export const uiToolPartSchema = z
  .object({
    /**
     * Set when a still-open pause was closed because the user walked away from
     * the turn rather than answering it. The `state` stays one of the SDK's
     * terminal states so the part remains replayable, and this marks it as
     * skipped: without it an abandoned approval is indistinguishable from a
     * deliberate denial, and an abandoned question from a tool failure.
     */
    abandonedReason: z.string().optional(),
    approval: toolApprovalSchema.optional(),
    errorText: z.string().optional(),
    input: z.unknown().optional(),
    nodeId: z.number().optional(),
    output: z.unknown().optional(),
    state: uiToolPartStateSchema,
    toolCallId: z.string(),
    toolName: z.string().optional(),
    type: uiToolPartTypeSchema,
  })
  .passthrough()
  .superRefine((part, ctx) => {
    if (part.state === 'output-available' && part.output === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'output-available requires output',
        path: ['output'],
      });
    }
    if (part.state === 'output-error' && typeof part.errorText !== 'string') {
      ctx.addIssue({
        code: 'custom',
        message: 'output-error requires errorText',
        path: ['errorText'],
      });
    }
    if (part.state === 'approval-requested' && !part.approval) {
      ctx.addIssue({
        code: 'custom',
        message: 'approval-requested requires approval',
        path: ['approval'],
      });
    }
    if (
      (part.state === 'approval-responded' || part.state === 'output-denied') &&
      typeof part.approval?.approved !== 'boolean'
    ) {
      ctx.addIssue({
        code: 'custom',
        message: `${part.state} requires approval.approved`,
        path: ['approval', 'approved'],
      });
    }
  });

/**
 * A persisted UI message part: a text part, a tool part, or any other AI SDK
 * part type (reasoning, file, …) kept intact via the trailing passthrough so
 * round-tripping never drops data.
 */
/**
 * Exported so continuation validators can normalize a raw persisted part and
 * a schema-parsed client echo through the identical pipeline before comparing
 * them — asymmetric normalization would make byte-equality checks reject
 * legitimately unchanged siblings.
 */
export const uiMessagePartSchema = z.union([
  uiTextPartSchema,
  uiToolPartSchema,
  z.object({ type: z.string() }).passthrough(),
]);

export const aiSdkUiMessageSchema = z.object({
  id: z.string(),
  parts: z.array(uiMessagePartSchema),
  role: uiMessageRoleSchema,
});

export const aiSdkUiMessagesSchema = z.array(aiSdkUiMessageSchema);

export const toolApprovalResponseSchema = z.object({
  approved: z.boolean(),
  id: z.string(),
  /**
   * Optional tool-input override. On approve it is what the tool executes,
   * for a card that collects values the model omitted (e.g. assignee on
   * workflowAssignTask) or edits the whole input (the report plan). On a deny
   * with a reason it is the input as the user edited it before asking for
   * changes, so the revision keeps those edits. The server decides which
   * tools may send either.
   */
  input: z.unknown().optional(),
  messageId: z.string().optional(),
  reason: z.string().optional(),
});
export const toolOutputResponseSchema = z.object({
  errorText: z.string().optional(),
  messageId: z.string().optional(),
  output: z.unknown().optional(),
  state: z.enum(['output-available', 'output-error']).optional(),
  tool: z.string(),
  toolCallId: z.string(),
});

export type AiSdkUiMessage = z.infer<typeof aiSdkUiMessageSchema>;
export type AiSdkUiMessagePart = z.infer<typeof uiMessagePartSchema>;
export type AiSdkUiToolPart = z.infer<typeof uiToolPartSchema>;
export type AiSdkUiToolPartState = z.infer<typeof uiToolPartStateSchema>;
export type ToolApproval = z.infer<typeof toolApprovalSchema>;
export type ToolApprovalResponse = z.infer<typeof toolApprovalResponseSchema>;
export type ToolOutputResponse = z.infer<typeof toolOutputResponseSchema>;
type UiMessageRole = z.infer<typeof uiMessageRoleSchema>;

const MISSING_TOOL_RESPONSE_TARGET_ERROR_NAME = 'MissingToolResponseTargetError';

export class MissingToolResponseTargetError extends Error {
  constructor(target: 'tool approval response' | 'tool output', id: string) {
    super(`Could not find ${target} target: ${id}`);
    this.name = MISSING_TOOL_RESPONSE_TARGET_ERROR_NAME;
  }
}

export function getIsMissingToolResponseTargetError(
  error: unknown
): error is MissingToolResponseTargetError {
  return error instanceof Error && error.name === MISSING_TOOL_RESPONSE_TARGET_ERROR_NAME;
}

/**
 * The shape `convertToModelMessages` consumes. We emit `dynamic-tool` parts so
 * the SDK can reconstruct tool calls / approvals / results without each tool
 * being registered in the set. This deliberately does not import the `ai`
 * package — common stays SDK-free; the server feeds this into the SDK.
 */
type ModelMessageInputTextPart = { text: string; type: 'text' };
type ModelMessageInputToolPart = {
  toolCallId: string;
  toolName: string;
  type: 'dynamic-tool';
} & (
  | {
      approval: { approved: boolean; id: string; reason?: string };
      input: unknown;
      state: 'approval-responded';
    }
  | {
      input: unknown;
      output: unknown;
      state: 'output-available';
      approval?: { approved: true; id: string; reason?: string };
    }
  | {
      errorText: string;
      input: unknown;
      state: 'output-error';
      approval?: { approved: true; id: string; reason?: string };
    }
  | {
      approval: { approved: false; id: string; reason?: string };
      input: unknown;
      state: 'output-denied';
    }
);
type ModelMessageInputPart = ModelMessageInputTextPart | ModelMessageInputToolPart;
export type ModelMessageInput = { parts: ModelMessageInputPart[]; role: UiMessageRole };

export interface AlineUiMessageSource {
  id: string;
  role: string;
  text: string;
  data?: {
    uiParts?: unknown[] | null;
  } | null;
}

export function toUiMessages(agentMessages: AlineUiMessageSource[]): AiSdkUiMessage[] {
  return agentMessages.flatMap(message => {
    const role = getUiRole(message.role);
    if (!role) return [];

    const parts = parseUiMessageParts(message.data?.uiParts, message.text);

    return [{ id: message.id, parts, role }];
  });
}

export function patchApprovalResponse(messages: AiSdkUiMessage[], input: ToolApprovalResponse) {
  let patched = false;
  const nextMessages = messages.map(message => {
    if (input.messageId && message.id !== input.messageId) return message;

    const nextParts = message.parts.map(part => {
      const nextPart = patchPartApprovalResponse(part, input);
      if (nextPart !== part) patched = true;
      return nextPart;
    });

    if (nextParts === message.parts) return message;
    return { ...message, parts: nextParts };
  });

  if (!patched) throw new MissingToolResponseTargetError('tool approval response', input.id);

  return nextMessages;
}

export function patchToolOutput(messages: AiSdkUiMessage[], input: ToolOutputResponse) {
  let patched = false;
  const nextMessages = messages.map(message => {
    if (input.messageId && message.id !== input.messageId) return message;

    const nextParts = message.parts.map(part => {
      const nextPart = patchPartToolOutput(part, input);
      if (nextPart !== part) patched = true;
      return nextPart;
    });

    return { ...message, parts: nextParts };
  });

  if (!patched) {
    throw new MissingToolResponseTargetError('tool output', input.toolCallId);
  }

  return nextMessages;
}

export function getUiMessagePartsPatch(message: AiSdkUiMessage) {
  return { uiParts: message.parts };
}

export function getPendingToolApprovalForToolCall(
  uiParts: unknown[] | null | undefined,
  toolCallId: string
) {
  const approval = getToolApprovalForToolCall(uiParts, toolCallId);
  if (!approval) return null;
  if (approval.approved !== undefined) return null;

  return { id: approval.id, part: approval.part };
}

export function getToolApprovalForToolCall(
  uiParts: unknown[] | null | undefined,
  toolCallId: string
) {
  for (const part of parseToolParts(uiParts)) {
    if (part.toolCallId !== toolCallId) continue;
    if (!part.approval) continue;

    return {
      approved: part.approval.approved,
      id: part.approval.id,
      part,
      reason: part.approval.reason,
    };
  }

  return null;
}

export function getToolOutputForToolCall(
  uiParts: unknown[] | null | undefined,
  toolCallId: string
) {
  for (const part of parseToolParts(uiParts)) {
    if (part.toolCallId !== toolCallId) continue;
    if (part.state !== 'output-available' && part.state !== 'output-error') continue;

    return {
      errorText: part.errorText,
      output: part.output,
      part,
      state: part.state,
    };
  }

  return null;
}

export function getPendingToolApproval(uiParts: unknown[] | null | undefined) {
  for (const part of parseToolParts(uiParts)) {
    if (!part.approval) continue;
    if (part.approval.approved !== undefined) continue;
    /**
     * System-gated waits (e.g. document extraction) auto-resolve; the user has
     * nothing to answer, so never surface them as a pending approval.
     */
    if (part.approval.system) continue;

    return { id: part.approval.id, part, toolCallId: part.toolCallId };
  }

  return null;
}

export function getPendingClientToolInput(uiParts: unknown[] | null | undefined) {
  for (const part of parseToolParts(uiParts)) {
    if (part.state !== 'input-available') continue;
    return { part, toolCallId: part.toolCallId };
  }

  return null;
}

/**
 * Whether a plan node left a tool part awaiting the user — an unanswered
 * approval request or a client tool (askUser) awaiting input. Used by the
 * harness to detect that a node paused and the turn should suspend.
 */
export function getIsNodeAwaitingInput(uiParts: unknown[] | null | undefined, nodeId: number) {
  return parseToolParts(uiParts).some(
    part =>
      part.nodeId === nodeId &&
      (part.state === 'approval-requested' || part.state === 'input-available')
  );
}

/**
 * Whether a message still has any tool part awaiting the user — an unanswered
 * approval or a client tool (askUser) awaiting input, regardless of node. The
 * continuation resolver uses this to avoid resuming a turn while a sibling
 * pause is still open: the resume replays the whole message, so an unresolved
 * pause would become a tool-call with no result (AI_MissingToolResultsError).
 */
export function getHasUnresolvedToolPause(uiParts: unknown[] | null | undefined) {
  return parseToolParts(uiParts).some(
    part => part.state === 'approval-requested' || part.state === 'input-available'
  );
}

/**
 * Resolve every still-open pause on a message to a terminal, non-resumable
 * state. Used when a turn is abandoned — e.g. the user sends a new prompt while
 * it was suspended: leaving the pause open would let the stale card resurface
 * (see `getPendingToolApproval` / `getPendingClientToolInput`) and let a late
 * answer half-resume a turn whose plan is already terminal. An unanswered
 * approval becomes denied; a client tool awaiting input (askUser) becomes an
 * errored output carrying `reason`. Both keep `abandonedReason` so the skip
 * stays distinguishable from a real denial or tool failure. Parts that aren't
 * open pauses are returned unchanged.
 */
export function abandonUnresolvedToolPauses(
  parts: AiSdkUiMessagePart[],
  reason: string
): AiSdkUiMessagePart[] {
  return parts.map(part => {
    const toolPart = getToolPart(part);
    if (!toolPart) return part;

    if (toolPart.state === 'approval-requested' && toolPart.approval) {
      return {
        ...toolPart,
        abandonedReason: reason,
        approval: { ...toolPart.approval, approved: false, reason },
        state: 'output-denied',
      } satisfies AiSdkUiToolPart;
    }

    if (toolPart.state === 'input-available' || toolPart.state === 'input-streaming') {
      return {
        ...toolPart,
        abandonedReason: reason,
        errorText: reason,
        output: undefined,
        state: 'output-error',
      } satisfies AiSdkUiToolPart;
    }

    return part;
  });
}

/**
 * Locate the assistant message carrying the user's continuation response (an
 * answered approval or a provided tool output) and the id that identifies it.
 * `nodeId` is the owning plan node, used to resume the right harness node.
 *
 * Scans newest-first: one turn can pause more than once (e.g. an `askUser`
 * answer that resumes into a later approval), leaving several answered parts on
 * the same assistant message. The harness is suspended on the most recent
 * pause, so the last answered part — not the first, already-consumed one — is
 * the correct resume target.
 */
export function findContinuationResponse(messages: AiSdkUiMessage[]) {
  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex--) {
    const message = messages[messageIndex];
    for (let partIndex = message.parts.length - 1; partIndex >= 0; partIndex--) {
      const toolPart = getToolPart(message.parts[partIndex]);
      if (!toolPart) continue;

      const continuationId = getContinuationResponseId(toolPart);
      if (!continuationId) continue;

      return {
        continuationId,
        messageId: message.id,
        nodeId: typeof toolPart.nodeId === 'number' ? toolPart.nodeId : null,
      };
    }
  }

  return null;
}

/**
 * The synthetic continuation id for a client-tool answer, which has no
 * approval id of its own. Every producer and comparison of these ids goes
 * through {@link getToolOutputContinuationId} so the prefix has exactly one
 * definition.
 */
export type ToolOutputContinuationId = `tool-output:${string}`;

export function getToolOutputContinuationId(toolCallId: string) {
  return `tool-output:${toolCallId}` satisfies ToolOutputContinuationId;
}

/**
 * The id used to key a continuation. An answered approval uses the approval
 * id; a client-provided tool output (e.g. askUser) has no approval id, so we
 * derive a stable synthetic id from the tool call.
 */
export function getContinuationResponseId(part: AiSdkUiToolPart): string | null {
  if (part.approval && typeof part.approval.approved === 'boolean') return part.approval.id;
  if (part.state === 'output-available' && part.output !== undefined)
    return getToolOutputContinuationId(part.toolCallId);
  if (part.state === 'output-error' && typeof part.errorText === 'string')
    return getToolOutputContinuationId(part.toolCallId);

  return null;
}

export function getNodeIdForApproval(messages: AiSdkUiMessage[], approvalId: string) {
  for (const message of messages) {
    for (const part of message.parts) {
      const toolPart = getToolPart(part);
      if (!toolPart) continue;
      if (
        toolPart.approval?.id !== approvalId &&
        getContinuationResponseId(toolPart) !== approvalId
      ) {
        continue;
      }

      return typeof toolPart.nodeId === 'number' ? toolPart.nodeId : null;
    }
  }

  return null;
}

/**
 * Normalize persisted UI messages into the `dynamic-tool` part shape that the
 * AI SDK's `convertToModelMessages` consumes. Incomplete tool calls still
 * awaiting the user are dropped — both an `askUser` still `input-available` and
 * an approval still `approval-requested`. Replaying an unanswered part would
 * emit a tool-call with no result, which `convertToLanguageModelPrompt` rejects
 * with `AI_MissingToolResultsError` (hit when one assistant step raises several
 * approvals and the user answers them one at a time). Only resolved parts —
 * answered approvals, denials, and tool outputs — replay.
 */
export function toModelMessageInput(messages: AiSdkUiMessage[]): ModelMessageInput[] {
  return messages.map(message => ({
    parts: message.parts.flatMap(toModelMessageInputPart),
    role: message.role,
  }));
}

function toModelMessageInputPart(part: AiSdkUiMessagePart): ModelMessageInputPart[] {
  if (getIsTextPart(part)) return [{ text: part.text, type: 'text' }];

  const toolPart = getToolPart(part);
  if (!toolPart?.toolName) return [];

  const base = {
    toolCallId: toolPart.toolCallId,
    toolName: toolPart.toolName,
    type: 'dynamic-tool',
  } as const;
  const input = toolPart.input;
  const approval = toolPart.approval;

  if (toolPart.state === 'output-available' && toolPart.output !== undefined) {
    return [
      {
        ...base,
        approval: approval?.approved === true ? { approved: true, id: approval.id } : undefined,
        input,
        output: toolPart.output,
        state: 'output-available',
      },
    ];
  }

  if (toolPart.state === 'output-error' && typeof toolPart.errorText === 'string') {
    return [
      {
        ...base,
        approval: approval?.approved === true ? { approved: true, id: approval.id } : undefined,
        errorText: toolPart.errorText,
        input,
        state: 'output-error',
      },
    ];
  }

  if (toolPart.state === 'output-denied' && approval) {
    return [
      {
        ...base,
        approval: { approved: false, id: approval.id, reason: approval.reason },
        input,
        state: 'output-denied',
      },
    ];
  }

  if (approval && typeof approval.approved === 'boolean') {
    return [
      {
        ...base,
        approval: { approved: approval.approved, id: approval.id, reason: approval.reason },
        input,
        state: 'approval-responded',
      },
    ];
  }

  /*
   * Anything left is a tool call still awaiting the user — an unanswered
   * approval (`approval-requested`, approval present but `approved` unset) or a
   * client tool (`input-available` / `input-streaming`). Drop it: replaying a
   * tool-call with no result crashes the resumed turn with
   * AI_MissingToolResultsError. Once the user answers, the part becomes
   * approval-responded / output-denied / output-available and replays above.
   */
  return [];
}

function getLegacyTextParts(text: string): AiSdkUiMessagePart[] {
  if (!text) return [];
  return [{ text, type: 'text' }];
}

function getUiRole(role: string): UiMessageRole | null {
  if (role === 'ASSISTANT') return UI_MESSAGE_ROLES.assistant;
  if (role === 'USER') return UI_MESSAGE_ROLES.user;
  return null;
}

function patchPartApprovalResponse(part: AiSdkUiMessagePart, input: ToolApprovalResponse) {
  const toolPart = getToolPart(part);
  if (toolPart?.approval?.id !== input.id) return part;

  /*
   * Approve → 'approval-responded' so the SDK executes the tool on replay.
   * Deny → 'output-denied' so `convertToModelMessages` emits a denial tool
   * result; an 'approval-responded' with approved:false would leave the
   * tool-call with no result and break the resumed model transcript.
   * Either response may replace `input` with what the card collected: an
   * approval when the card filled values the model left unset (assignee
   * picker, etc.), a denial when an editable card sends the input as the
   * user edited it along with their request for changes.
   */
  return {
    ...toolPart,
    approval: { approved: input.approved, id: toolPart.approval.id, reason: input.reason },
    state: input.approved ? 'approval-responded' : 'output-denied',
    ...(input.input !== undefined ? { input: input.input } : {}),
  } satisfies AiSdkUiToolPart;
}

function patchPartToolOutput(part: AiSdkUiMessagePart, input: ToolOutputResponse) {
  const toolPart = getToolPart(part);
  if (!toolPart || toolPart.toolCallId !== input.toolCallId) return part;

  const toolName = toolPart.toolName ?? input.tool;
  const state = input.state ?? 'output-available';

  if (state === 'output-error') {
    return {
      ...toolPart,
      errorText: input.errorText ?? 'Tool output failed.',
      output: undefined,
      state,
      toolName,
    } satisfies AiSdkUiToolPart;
  }

  return {
    ...toolPart,
    errorText: undefined,
    output: input.output,
    state,
    toolName,
  } satisfies AiSdkUiToolPart;
}

function parseToolParts(uiParts: unknown[] | null | undefined): AiSdkUiToolPart[] {
  if (!Array.isArray(uiParts)) return [];
  return uiParts.flatMap(part => {
    const parsed = uiToolPartSchema.safeParse(part);
    return parsed.success ? [parsed.data] : [];
  });
}

function parseUiMessageParts(
  uiParts: unknown[] | null | undefined,
  legacyText: string
): AiSdkUiMessagePart[] {
  if (!Array.isArray(uiParts)) return getLegacyTextParts(legacyText);

  const parts = uiParts.flatMap(part => {
    const parsed = uiMessagePartSchema.safeParse(part);
    return parsed.success ? [parsed.data] : [];
  });
  return parts.length ? parts : getLegacyTextParts(legacyText);
}

/**
 * The single narrowing guard for tool parts in the AiSdkUiMessagePart union — consumers must use
 * this instead of hand-rolling `'toolCallId' in part`. Deliberately structural rather than
 * schema-based: uiToolPartSchema's refinements (e.g. output-available requires output) would
 * reject a mid-stream or partially-written tool part that callers matching on toolCallId still
 * need to recognize.
 */
export function getIsToolPart(part: AiSdkUiMessagePart): part is AiSdkUiToolPart {
  return 'toolCallId' in part;
}

function getToolPart(part: AiSdkUiMessagePart): AiSdkUiToolPart | null {
  const parsed = uiToolPartSchema.safeParse(part);
  return parsed.success ? parsed.data : null;
}

function getIsTextPart(part: AiSdkUiMessagePart): part is { text: string; type: 'text' } {
  return part.type === 'text' && 'text' in part && typeof part.text === 'string';
}
